import React, { useState, useEffect, useContext } from 'react';
import '../../css/dictionary.css';
import { functionContext } from '../../App';

function Dictionary() {

  const { selectedWord, selectedLanguage, setSelectedLanguage } = useContext(functionContext);
  const [definition, setDefinition] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [retryNonce, setRetryNonce] = useState(0);
  const [lastProvider, setLastProvider] = useState('');

  const backendBaseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:8080";

  function stripTags(str = '') {
    return str
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&quot;/g, '"')
      .trim();
  }

  function parsePonsGermanDefinition(data) {
    if (!Array.isArray(data) || !data.length) return null;

    // Helper to pull the first definition-like string we find in translations
    const pickTranslation = (translations = []) => {
      for (const t of translations) {
        const source = typeof t?.source === 'string' ? t.source : '';
        const target = typeof t?.target === 'string' ? t.target : '';

        // Prefer explicit definition spans
        const defMatch =
          (source.match(/<span[^>]*class="definition"[^>]*>(.*?)<\/span>/i)?.[1]) ||
          (target.match(/<span[^>]*class="definition"[^>]*>(.*?)<\/span>/i)?.[1]);
        if (defMatch) return stripTags(defMatch);

        if (source.trim()) return stripTags(source);
        if (target.trim()) return stripTags(target);
      }
      return null;
    };

    for (const entry of data) {
      const hits = entry?.hits;
      if (!Array.isArray(hits)) continue;

      for (const hit of hits) {
        if (hit?.type === 'translation') {
          const translated = pickTranslation([hit]);
          if (translated) return translated;
        }

        if (hit?.type === 'entry' || hit?.type === 'entry_with_secondary_entries') {
          const roms = hit?.roms;
          if (!Array.isArray(roms)) continue;
          for (const rom of roms) {
            const arabs = rom?.arabs;
            if (!Array.isArray(arabs)) continue;
            for (const arab of arabs) {
              const translated = pickTranslation(arab?.translations);
              if (translated) return translated;
            }
          }
        }
      }
    }

    return null;
  }

  const providers = {
    en: [
      {
        name: 'dictionaryapi.dev',
        url: (word) => `https://api.dictionaryapi.dev/api/v2/entries/en/${encodeURIComponent(word)}`,
        parse: (data) => data?.[0]?.meanings?.[0]?.definitions?.[0]?.definition,
      },
    ],
    de: [
      {
        name: 'pons',
        url: (word) =>
          `${backendBaseUrl}/pons?word=${encodeURIComponent(word)}`,
        parse: (data) => parsePonsGermanDefinition(data),
      },
    ],
  };

  async function fetchFromProviders(word, lang, signal) {
    const list = providers[lang] || providers.en;
    let lastError = null;

    for (const provider of list) {
      try {
        const res = await fetch(provider.url(word), {
          signal,
          headers: provider.headers,
        });
        if (!res.ok) {
          lastError = `${provider.name}: ${res.status}`;
          continue;
        }
        const data = await res.json();
        const parsed = provider.parse(data);
        if (parsed) {
          setLastProvider(provider.name);
          return { result: parsed };
        }
        lastError = `${provider.name}: no definition found`;
      } catch (err) {
        if (err.name === 'AbortError') throw err;
        lastError = `${provider.name}: ${err.message}`;
      }
    }

    return { result: null, error: lastError || 'No definition found' };
  }

  useEffect(() => {
    if (!selectedWord) {
      setDefinition('');
      setError('');
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    const fetchDefinition = async () => {
      try {
        setLoading(true);
        setError('');
        const { result, error: providerError } = await fetchFromProviders(selectedWord, selectedLanguage, controller.signal);
        if (result) {
          setDefinition(result);
          return;
        }
        throw new Error(providerError || 'No definition found');
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError(err.message || 'Could not load definition.');
          setDefinition('');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDefinition();

    return () => controller.abort();
  }, [selectedWord, selectedLanguage, retryNonce]);

  const showPlaceholder = !selectedWord;

  return (
    <>
      <div id="dictionary">
        <div className="dictionary-header">
          <div className="dictionary-word">
            {showPlaceholder ? 'Tap a card to see its definition' : selectedWord}
          </div>
          <div className="dictionary-toggle">
            <button
              disabled={loading}
              className={`toggle-btn ${selectedLanguage === 'en' ? 'active' : ''}`}
              onClick={() => setSelectedLanguage('en')}
            >
              English
            </button>
            <button
              disabled={loading}
              className={`toggle-btn ${selectedLanguage === 'de' ? 'active' : ''}`}
              onClick={() => setSelectedLanguage('de')}
            >
              German
            </button>
          </div>
        </div>
        <div className="dictionary-body">
          {loading && (
            <p className="dictionary-status">Loading…</p>
          )}
          {error && (
            <div className="dictionary-status">
              <p>{error}</p>
              {lastProvider && <p className="dictionary-provider">Last tried: {lastProvider}</p>}
              <button className="dictionary-retry" onClick={() => setRetryNonce((n) => n + 1)}>Retry</button>
            </div>
          )}
          {!loading && !error && !showPlaceholder && (
            <p className="dictionary-definition">{definition}</p>
          )}
        </div>
      </div>
    </>
  )
}

export default Dictionary;
